// Loosely based on ts-two-d-tree by Clinton Bush.
// https://www.npmjs.com/package/ts-two-d-tree
//
// Original licence:
/*
MIT License

Copyright (c) 2023 Clinton Bush

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

export type Point3 = [number, number, number];

function euclideanDistance2(
  p1_0: number,
  p1_1: number,
  p2_0: number,
  p2_1: number,
): number {
  var dx = p1_0 - p2_0,
    dy = p1_1 - p2_1;
  return dx * dx + dy * dy;
}

class KNNNode {
  min0: number = +Number.MAX_VALUE;
  max0: number = -Number.MAX_VALUE;
  min1: number = +Number.MAX_VALUE;
  max1: number = -Number.MAX_VALUE;
  search(
    x: number,
    y: number,
    distance: number,
    results: Array<[number, Point3]>,
  ): void {}
}

class KNNBranch extends KNNNode {
  private _children: KNNNode[] = [];

  search(
    x: number,
    y: number,
    distance: number,
    results: Array<[number, Point3]>,
  ): void {
    var in_0: boolean = this.min0 <= x && x <= this.max0;
    var in_1: boolean = this.min1 <= y && y <= this.max1;

    if (in_0) {
      if (!in_1) {
        // Within X range, outside Y range
        if (
          Math.min(Math.abs(y - this.min1), Math.abs(y - this.max1)) > distance
        ) {
          return;
        }
      }
    } else {
      if (in_1) {
        // Within Y range, outside X range
        if (
          Math.min(Math.abs(x - this.min0), Math.abs(x - this.max0)) > distance
        ) {
          return;
        }
      } else {
        // Outside both ranges, so compute distance to the nearest corner.
        var d = euclideanDistance2(x, y, this.min0, this.min1);
        d = Math.min(d, euclideanDistance2(x, y, this.min0, this.max1));
        d = Math.min(d, euclideanDistance2(x, y, this.max0, this.min1));
        d = Math.min(d, euclideanDistance2(x, y, this.max0, this.max1));
        if (d > distance * distance) {
          return;
        }
      }
    }

    this._children.forEach((c) => {
      c.search(x, y, distance, results);
    });
  }

  addChild(child: KNNNode) {
    this.min0 = Math.min(this.min0, child.min0);
    this.max0 = Math.max(this.max0, child.max0);
    this.min1 = Math.min(this.min1, child.min1);
    this.max1 = Math.max(this.max1, child.max1);
    this._children.push(child);
  }
}

class KNNLeaf extends KNNNode {
  private _points: Point3[];

  constructor(points: Point3[]) {
    super();
    this._points = points;
    this.min0 = points[0][0];
    this.max0 = points[0][0];
    this.min1 = points[0][1];
    this.max1 = points[0][1];
    for (var i = 1; i < points.length; ++i) {
      this.min0 = Math.min(this.min0, points[i][0]);
      this.max0 = Math.max(this.max0, points[i][0]);
      this.min1 = Math.min(this.min1, points[i][1]);
      this.max1 = Math.max(this.max1, points[i][1]);
    }
  }

  search(
    x: number,
    y: number,
    distance: number,
    results: Array<[number, Point3]>,
  ): void {
    let distance2 = distance * distance;
    this._points.forEach((p) => {
      var d2 = euclideanDistance2(x, y, p[0], p[1]);
      if (d2 <= distance2) {
        results.push([d2, p]);
      }
    });
  }
}

export class KNN {
  private _root: KNNNode;

  /**
   * Creates a new KNN index.
   * @param points Set of points to be added to the index.
   */
  constructor(points: Point3[]) {
    this._root = this.buildStack(points, 0);
  }

  private buildStack(points: Point3[], depth: number): KNNNode | undefined {
    const axis = this.getAxisIndex(depth);

    points.sort((p1, p2) => p1[axis] - p2[axis]);

    const median = Math.floor((points.length - 1) / 2);

    if (points.length > 10) {
      var node = new KNNBranch();
      node.addChild(this.buildStack(points.slice(0, median), depth + 1));
      node.addChild(this.buildStack(points.slice(median), depth + 1));
      return node;
    } else if (points.length > 0) {
      return new KNNLeaf(points);
    } else {
      return undefined;
    }
  }

  /**
   * Perform a k nearest-neighbour search.
   *
   * @param p Query point
   * @param distance Maximum distance to search
   * @param limit Maximum number of points to return
   * @returns the results
   */
  knnSearch(
    x: number,
    y: number,
    distance: number,
    limit?: number,
  ): Array<[number, Point3]> {
    var results = new Array<[number, Point3]>();

    this._root.search(x, y, distance, results);

    return results.sort((a, b) => a[0] - b[0]).slice(0, limit);
  }

  private getAxisIndex(depth: number): number {
    return depth % 2;
  }
}
